import React from 'react';
import type { FC } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ac } from 'react-router/dist/development/route-data-Cw8htKcF';

export interface TabItem {
    id: string;
    name: string;
}

export interface TabBarProps {
    tabs: TabItem[];
    selectedTab: string;
    onTabChange?: (id: string) => void;
}

export const TabBar: FC<TabBarProps> = ({ tabs, selectedTab, onTabChange }) => {
    const tabsRef = useRef<(HTMLElement | null)[]>([]);
    const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
    const [tabUnderlineWidth, setTabUnderlineWidth] = useState<number | undefined>();
    const [tabUnderlineLeft, setTabUnderlineLeft] = useState<number | undefined>();

    function getRef(element: HTMLButtonElement | null, index: number) {
        tabsRef.current[index] = element;
    }

    useEffect(() => {
        const initialIndex = tabs.findIndex((tab) => tab.id === selectedTab);
        setActiveTabIndex(initialIndex >= 0 ? initialIndex : 0);
    }, [selectedTab, tabs]);

    useLayoutEffect(() => {
        if (activeTabIndex === null) return;
        const currentTab = tabsRef.current[activeTabIndex] as HTMLElement;
        setTabUnderlineLeft(currentTab?.offsetLeft ?? 0);
        setTabUnderlineWidth(currentTab?.clientWidth ?? 0);
    }, [activeTabIndex]);

    return (
        <div className="flex-row relative mx-auto flex rounded-3xl bg-gray-100 hover:bg-gray-90 px-1 backdrop-blur-sm">
            <span
                className="absolute bottom-0 top-0 flex overflow-hidden rounded-3xl py-1 transition-all duration-300"
                style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
            >
                <span className="h-full w-full rounded-3xl bg-blue-00 z-10" />
            </span>
            {tabs.map((tab, index) => {
                const isActive = activeTabIndex === index;
                const element = (
                    <button
                        type="button"
                        key={tab.id}
                        ref={(el) => getRef(el, index)}
                        className={`${
                            isActive ? 'text-black-50 font-medium' : 'text-gray-50 font-light'
                        } z-20 flex-grow cursor-pointer select-none rounded-full px-10 py-2 text-center text-sm`}
                        onClick={() => {
                            setActiveTabIndex(index);
                            onTabChange?.(tab.id);
                        }}
                    >
                        {tab.name}
                    </button>
                );
                return element;
            })}
        </div>
    );
};
